import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BookOpen, Plus, ArrowLeft, Bell, Settings, CheckCircle, Lock,
  FileText, AlertTriangle, ChevronRight, Loader2, BarChart3, Download
} from 'lucide-react';
import { STEPS, STATUS_COLORS } from './DiaryWizard';
import { api } from '@/api';
import { toast } from 'sonner';

export default function DiaryListView({
  project, projectId, diaries, loading, stats,
  selectedDate, setSelectedDate,
  sectionAssignments, openWizard, setCurrentStep,
  createDiary, lockDiary,
  openNotifSettings, openSectionAssign,
}) {
  const navigate = useNavigate();

  const exportPdf = async (diaryId, diaryDate) => {
    try {
      toast.info('Generating PDF...');
      const response = await api.get(`/diary/${diaryId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Site_Diary_${diaryDate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('PDF export failed');
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="site-diary-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/projects/${projectId}`)} className="p-2 rounded-lg hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BookOpen className="h-6 w-6 text-cyan-600" /> Daily Site Diary</h1>
            <p className="text-sm text-slate-500 mt-1">{project?.name || ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-40" data-testid="diary-date-input" />
          <Button onClick={openNotifSettings} variant="outline" title="Notification Settings" data-testid="diary-notif-settings-btn">
            <Bell className="h-4 w-4" />
          </Button>
          <Button onClick={openSectionAssign} variant="outline" title="Section Assignments" data-testid="diary-section-assign-btn">
            <Settings className="h-4 w-4" />
          </Button>
          <Button onClick={createDiary} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-diary-btn">
            <Plus className="h-4 w-4 mr-1" /> New Diary
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total Diaries', value: stats.total, icon: BookOpen, color: 'text-cyan-600' },
            { label: 'Submitted', value: stats.submitted, icon: CheckCircle, color: 'text-emerald-600' },
            { label: 'Locked', value: stats.locked, icon: Lock, color: 'text-slate-600' },
            { label: 'Drafts', value: stats.draft, icon: FileText, color: 'text-blue-600' },
            { label: 'With Delays', value: stats.with_delays, icon: AlertTriangle, color: 'text-red-600' },
          ].map((s, i) => (
            <Card key={i}><CardContent className="p-3 flex items-center gap-2"><s.icon className={`h-6 w-6 ${s.color}`} /><div><p className="text-[10px] text-slate-500">{s.label}</p><p className="text-lg font-bold">{s.value}</p></div></CardContent></Card>
          ))}
        </div>
      )}

      {/* Today's Section Status Board */}
      {Object.keys(sectionAssignments).length > 0 && (() => {
        const today = new Date().toISOString().split('T')[0];
        const todayDiary = diaries.find(d => d.date === today || d.date === selectedDate);
        const ss = todayDiary?.section_status || {};
        const assignedSections = STEPS.filter(s => s.id !== 'ai_summary' && s.id !== 'submit' && sectionAssignments[s.id]?.user_id);
        if (assignedSections.length === 0) return null;
        const completedCount = assignedSections.filter(s => ss[s.id]?.completed).length;
        const allDone = completedCount === assignedSections.length;
        return (
          <Card className={`border ${allDone ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`} data-testid="section-status-board">
            <CardHeader className="pb-2 pt-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-cyan-600" />
                  Today's Diary — Section Status
                  <Badge className={`text-[10px] ml-1 ${allDone ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{completedCount}/{assignedSections.length}</Badge>
                </CardTitle>
                {allDone && <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle className="h-3 w-3 mr-1" /> Ready for Review</Badge>}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {assignedSections.map(step => {
                  const Icon = step.icon;
                  const done = ss[step.id]?.completed;
                  const assignee = sectionAssignments[step.id];
                  return (
                    <div key={step.id}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 border cursor-pointer transition-all hover:shadow-sm ${done ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                      onClick={() => { if (todayDiary) { openWizard(todayDiary); setTimeout(() => setCurrentStep(STEPS.findIndex(s => s.id === step.id)), 100); } }}
                      data-testid={`status-${step.id}`}
                    >
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 ${done ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                        {done ? <CheckCircle className="h-3.5 w-3.5 text-white" /> : <Icon className="h-3 w-3 text-slate-500" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-800 truncate">{step.label}</p>
                        <p className={`text-[10px] truncate ${done ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {done ? ss[step.id].completed_by_name : assignee?.user_name || 'Unassigned'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Diary List */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Labour</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Delays</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Weather</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Sections</th>
              <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan="7" className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></td></tr>
            ) : diaries.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-12 text-slate-400">No diaries found. Create your first diary above.</td></tr>
            ) : diaries.map(d => {
              const stCls = STATUS_COLORS[d.status] || STATUS_COLORS.not_started;
              const w = d.weather;
              return (
                <tr key={d.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openWizard(d)} data-testid={`diary-row-${d.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{d.date}</td>
                  <td className="px-4 py-3"><Badge className={`${stCls} text-xs`}>{d.status?.replace(/_/g, ' ')}</Badge></td>
                  <td className="px-4 py-3 hidden md:table-cell">{d.labour?.total_labour || 0}</td>
                  <td className="px-4 py-3 hidden md:table-cell">{d.delays?.length || 0}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs">{w ? `${w.description || ''} ${w.rain ? '\u{1F327}' : ''}` : '-'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {(() => {
                      const ss = d.section_status || {};
                      const done = STEPS.filter(s => s.id !== 'ai_summary' && s.id !== 'submit' && ss[s.id]?.completed).length;
                      const total = STEPS.filter(s => s.id !== 'ai_summary' && s.id !== 'submit').length;
                      return (
                        <div className="flex items-center gap-1.5">
                          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${(done / total) * 100}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-500">{done}/{total}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => exportPdf(d.id, d.date)} className="p-1.5 rounded hover:bg-slate-100" title="Export PDF" data-testid={`export-pdf-${d.id}`}>
                        <Download className="h-3.5 w-3.5 text-cyan-600" />
                      </button>
                      {d.status !== 'locked' && (
                        <button onClick={() => lockDiary(d.id)} className="p-1.5 rounded hover:bg-slate-100" title="Lock"><Lock className="h-3.5 w-3.5 text-slate-500" /></button>
                      )}
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
